import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Compass, Landmark, Users } from "lucide-react";
import PartnerLogos from "../components/PartnerLogos";
import { useLang } from "../i18n/LanguageProvider";

const HERO_POSTER =
  "https://images.pexels.com/videos/32284115/pexels-photo-32284115.jpeg?auto=compress&w=1920";

// Aerial dusk view of Singapore's Marina Bay skyline — Pexels
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/32284115/13767949_2560_1440_30fps.mp4";

export default function Home() {
  const { t } = useLang();

  const pillars = [
    { icon: ShieldCheck, title: t("home.pillar1Title"), text: t("home.pillar1Text") },
    { icon: Compass,     title: t("home.pillar2Title"), text: t("home.pillar2Text") },
    { icon: Landmark,    title: t("home.pillar3Title"), text: t("home.pillar3Text") },
    { icon: Users,       title: t("home.pillar4Title"), text: t("home.pillar4Text") },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden">
        <div className="absolute inset-0">
          <video
            key={HERO_VIDEO}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={HERO_POSTER}
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.42) saturate(0.85)" }}
            data-testid="hero-video"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,13,20,0.35) 0%, rgba(10,13,20,0.7) 60%, rgba(10,13,20,1) 100%)",
            }}
          />
        </div>

        <div className="relative z-[2] max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 lg:pt-56 pb-24">
          <div className="max-w-4xl">
            <div className="eyebrow mb-8 fade-in-up">{t("home.eyebrow")}</div>
            <h1 className="font-serif-display text-[2.5rem] sm:text-6xl lg:text-7xl leading-[1.02] text-white fade-in-up delay-1">
              {t("home.headline1")} <br />
              <span className="text-gold italic font-normal">{t("home.headline2")}</span>
            </h1>
            <p className="mt-10 text-white/80 text-base sm:text-lg leading-relaxed max-w-2xl fade-in-up delay-2">
              {t("home.sublinePre")}<span className="text-gold">{t("home.aum")}</span>{t("home.sublinePost")}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 fade-in-up delay-3">
              <Link to="/contact" className="btn-gold" data-testid="hero-cta-consult">
                {t("home.cta1")}
              </Link>
              <Link to="/philosophy" className="btn-ghost">
                {t("home.cta2")} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-6 lg:left-12 right-6 lg:right-12 z-[2] flex items-end justify-between text-[10px] tracking-[0.35em] uppercase text-white/50">
          <div>{t("home.badgeLeft")}</div>
          <div className="hidden md:block">{t("home.badgeRight")}</div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-20 lg:py-24 border-b border-[var(--bw-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`fade-in-up delay-${(i % 4) + 1}`} data-testid={`stat-${i}`}>
              <div className="font-serif-display text-4xl lg:text-5xl text-gold mb-3">{t(`home.stat${i}Val`)}</div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-muted leading-relaxed">
                {t(`home.stat${i}Label`)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-6">{t("home.pillarsEyebrow")}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
              {t("home.pillarsTitle")}
            </h2>
            <p className="text-muted mt-6 leading-relaxed">{t("home.pillarsLede")}</p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <div key={p.title} className={`card-lux ${i % 2 === 1 ? "md:translate-y-8" : ""}`} data-testid={`pillar-${i}`}>
                <p.icon size={22} className="text-gold mb-6" strokeWidth={1.2} />
                <h3 className="font-serif-display text-2xl text-white mb-3">{p.title}</h3>
                <p className="text-muted text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER LOGOS */}
      <PartnerLogos />

      {/* STATEMENT */}
      <section className="relative py-24 lg:py-32 border-b border-[var(--bw-border)] bg-surface">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-2">
            <div className="eyebrow">{t("home.noteEyebrow")}</div>
          </div>
          <div className="lg:col-span-10">
            <blockquote className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-[1.25] text-white">
              &ldquo;{t("home.quote")}&rdquo;
            </blockquote>
            <div className="mt-8 text-sm tracking-[0.28em] uppercase text-muted">
              {t("home.quoteAttrib")}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="border border-[var(--bw-border)] p-10 md:p-16 lg:p-20 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[var(--bw-gold)] opacity-[0.06] blur-3xl" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="eyebrow mb-6">{t("home.ctaEyebrow")}</div>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
                  {t("home.ctaTitle")}
                </h2>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link to="/contact" className="btn-gold" data-testid="cta-request">
                  {t("home.ctaBtn")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
